import { Component, OnInit } from '@angular/core';
import { NgClass } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { SwitchPortDto, PortActionResult } from '../../models/switch-port.model';

type ActionType = 'learn' | 'block' | 'enable';

@Component({
  selector: 'app-switch-ports',
  standalone: true,
  imports: [NgClass, FormsModule],
  templateUrl: './switch-ports.component.html',
  styleUrls: ['./switch-ports.component.scss']
})
export class SwitchPortsComponent implements OnInit {
  // Port data
  ports: SwitchPortDto[] = [];
  loading = false;
  error: string | null = null;
  actionState: Record<number, ActionType | null> = {};
  actionResult: Record<number, { ok: boolean; msg: string } | null> = {};

  // Password setup
  isConfigured = true;
  passwordInput = '';
  savingPassword = false;
  passwordError: string | null = null;

  constructor(private apiService: ApiService) {}

  ngOnInit(): void {
    this.checkConfigAndLoad();
  }

  checkConfigAndLoad(): void {
    this.apiService.isSwitch51Configured().subscribe({
      next: (configured) => {
        this.isConfigured = configured;
        if (configured) this.loadPorts();
      },
      error: () => {
        // If the check itself fails, try loading ports anyway to surface the real error
        this.loadPorts();
      }
    });
  }

  savePassword(): void {
    if (!this.passwordInput.trim()) return;
    this.savingPassword = true;
    this.passwordError = null;
    this.apiService.saveSwitch51Password(this.passwordInput).subscribe({
      next: () => {
        this.savingPassword = false;
        this.isConfigured = true;
        this.passwordInput = '';
        this.loadPorts();
      },
      error: (err) => {
        this.savingPassword = false;
        this.passwordError = err?.error?.error ?? 'Failed to save password';
      }
    });
  }

  loadPorts(): void {
    this.loading = true;
    this.error = null;
    this.apiService.getSwitch51Ports().subscribe({
      next: (ports) => {
        this.ports = ports;
        this.loading = false;
      },
      error: (err) => {
        this.error = err?.error?.error ?? err?.message ?? 'Failed to connect to switch';
        this.loading = false;
      }
    });
  }

  learn(port: number): void {
    this.runAction(port, 'learn', () => this.apiService.learnSwitch51Port(port));
  }

  block(port: number): void {
    this.runAction(port, 'block', () => this.apiService.blockSwitch51Port(port));
  }

  enable(port: number): void {
    this.runAction(port, 'enable', () => this.apiService.enableSwitch51Port(port));
  }

  isDisabled(status: string): boolean {
    return status.toLowerCase() === 'disabled';
  }

  statusClass(status: string): string {
    const s = status.toLowerCase();
    if (s === 'up') return 'status-up';
    if (s === 'disabled') return 'status-disabled';
    return 'status-down';
  }

  private runAction(port: number, action: ActionType, call: () => import('rxjs').Observable<PortActionResult>): void {
    this.actionState[port] = action;
    this.actionResult[port] = null;
    call().subscribe({
      next: (result) => {
        this.actionState[port] = null;
        this.actionResult[port] = { ok: result.success, msg: result.message };
        if (result.success) this.loadPorts();
        setTimeout(() => { this.actionResult[port] = null; }, 4000);
      },
      error: (err) => {
        this.actionState[port] = null;
        this.actionResult[port] = { ok: false, msg: err?.error?.error ?? 'Action failed' };
        setTimeout(() => { this.actionResult[port] = null; }, 4000);
      }
    });
  }
}
